import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Medal, ChevronUp, ChevronDown, ChevronsUpDown, Ban, Undo2, Download } from 'lucide-react';
import { resultsApi } from '@/api/endpoints/results';
import { StageCompleter } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import { downloadBlob } from '@/utils/downloadBlob';

// ─── Types ────────────────────────────────────────────────────────────────────

type SortField = 'score' | 'totalScore' | 'durationFromStartClickMs' | 'focusEventCount' | 'disqualified';
type SortDir = 'asc' | 'desc';

type LeaderboardTab = 'stage1-t1' | 'stage1-t2' | 'stage2';

const TABS: { id: LeaderboardTab; label: string; stage: 1 | 2; term?: number }[] = [
  { id: 'stage1-t1', label: 'Etap 1 Termin 1', stage: 1, term: 1 },
  { id: 'stage1-t2', label: 'Etap 1 Termin 2', stage: 1, term: 2 },
  { id: 'stage2', label: 'Etap 2', stage: 2 },
];

function tabLabel(tab: LeaderboardTab): string {
  return TABS.find((t) => t.id === tab)?.label ?? '';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getMedalIcon = (rankAmongActive: number) => {
  if (rankAmongActive === 0) return <Medal className="h-5 w-5 text-amber-500" />;
  if (rankAmongActive === 1) return <Medal className="h-5 w-5 text-gray-400" />;
  if (rankAmongActive === 2) return <Medal className="h-5 w-5 text-amber-700" />;
  return <span className="text-sm font-semibold text-gray-500">#{rankAmongActive + 1}</span>;
};

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function totalTimeMs(c: StageCompleter): number {
  return (c.durationFromStartClickMs ?? Infinity) + (c.practicalDurationSeconds ?? 0) * 1000;
}

function sortCompleters(
  completers: StageCompleter[],
  field: SortField,
  dir: SortDir,
): StageCompleter[] {
  const sign = dir === 'asc' ? 1 : -1;

  return [...completers].sort((a, b) => {
    if (field === 'disqualified') {
      if (a.disqualified !== b.disqualified) {
        return (a.disqualified ? 1 : -1) * sign;
      }
      const ta = a.totalScore ?? a.score ?? 0;
      const tb = b.totalScore ?? b.score ?? 0;
      return tb - ta;
    }

    if (a.disqualified !== b.disqualified) return a.disqualified ? 1 : -1;

    let diff = 0;
    if (field === 'score') {
      diff = (a.score ?? 0) - (b.score ?? 0);
    } else if (field === 'totalScore') {
      const ta = a.totalScore ?? a.score ?? 0;
      const tb = b.totalScore ?? b.score ?? 0;
      diff = ta - tb;
    } else if (field === 'durationFromStartClickMs') {
      diff = (a.durationFromStartClickMs ?? Infinity) - (b.durationFromStartClickMs ?? Infinity);
    } else if (field === 'focusEventCount') {
      diff = a.focusEventCount - b.focusEventCount;
    }
    if (diff !== 0) return diff * sign;
    // Tie-break: total time asc (for score/totalScore sorts)
    if (field === 'score' || field === 'totalScore') {
      return totalTimeMs(a) - totalTimeMs(b);
    }
    return 0;
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SortHeaderProps {
  label: string;
  field: SortField;
  active: SortField;
  dir: SortDir;
  onSort: (field: SortField) => void;
  className?: string;
  defaultDir?: SortDir;
}

const SortHeader = ({ label, field, active, dir, onSort, className }: SortHeaderProps) => {
  const isActive = active === field;
  return (
    <TableHead
      className={cn('cursor-pointer select-none hover:bg-gray-50', className)}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          dir === 'desc' ? (
            <ChevronDown className="h-3.5 w-3.5 text-primary-600" />
          ) : (
            <ChevronUp className="h-3.5 w-3.5 text-primary-600" />
          )
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 text-gray-300" />
        )}
      </span>
    </TableHead>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const LeaderboardPage = ({ readOnly = false }: { readOnly?: boolean }) => {
  const [activeTab, setActiveTab] = useState<LeaderboardTab>(readOnly ? 'stage2' : 'stage1-t1');
  const activeConfig = TABS.find((t) => t.id === activeTab)!;
  const isStage2 = activeTab === 'stage2';
  const visibleTabs = readOnly ? TABS.filter((t) => t.id === 'stage2') : TABS;
  const [sortField, setSortField] = useState<SortField>(isStage2 ? 'totalScore' : 'score');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  // sessionId of the row awaiting disqualify confirmation; null = none
  const [confirmingSessionId, setConfirmingSessionId] = useState<string | null>(null);

  // Reset sort when switching tabs
  useEffect(() => {
    setSortField(isStage2 ? 'totalScore' : 'score');
    setSortDir('desc');
  }, [isStage2]);

  const queryClient = useQueryClient();

  const { data: completers, isLoading } = useQuery({
    queryKey: ['stage-completers', activeTab],
    queryFn: () => resultsApi.getStageCompleters(activeConfig.stage, activeConfig.term),
    refetchInterval: 30_000,
  });

  const disqualifyMutation = useMutation({
    mutationFn: ({ sessionId, disqualified }: { sessionId: string; disqualified: boolean }) =>
      resultsApi.disqualifySession(sessionId, disqualified),
    onSuccess: (_data, { disqualified }) => {
      queryClient.invalidateQueries({ queryKey: ['stage-completers', activeTab] });
      toast.success(disqualified ? 'Uczestnik zdyskwalifikowany' : 'Dyskwalifikacja cofnięta');
    },
    onError: () => {
      toast.error('Nie udało się zmienić statusu dyskwalifikacji');
    },
  });

  const exportWrongAnswersMutation = useMutation({
    mutationFn: () => resultsApi.exportWrongAnswersCsv(activeConfig.stage, activeConfig.term),
    onSuccess: (blob) => downloadBlob(blob, 'bledne_odpowiedzi.csv'),
    onError: () => toast.error('Nie udało się wyeksportować błędów'),
  });

  const exportAllAnswersMutation = useMutation({
    mutationFn: () => resultsApi.exportAllAnswersCsv(activeConfig.stage, activeConfig.term),
    onSuccess: (blob) => downloadBlob(blob, 'wszystkie_odpowiedzi.csv'),
    onError: () => toast.error('Nie udało się wyeksportować odpowiedzi'),
  });

  const exportFullMutation = useMutation({
    mutationFn: resultsApi.exportStage2FullCsv,
    onSuccess: (blob) => downloadBlob(blob, 'wyniki_lacznie_etap2.csv'),
    onError: () => toast.error('Nie udało się pobrać raportu'),
  });

  const exportTeorMutation = useMutation({
    mutationFn: () => resultsApi.exportStageCsv(2),
    onSuccess: (blob) => downloadBlob(blob, 'wyniki_teor_etap2.csv'),
    onError: () => toast.error('Nie udało się pobrać raportu'),
  });

  const exportPraktMutation = useMutation({
    mutationFn: resultsApi.exportPracticalScoresCsv,
    onSuccess: (blob) => downloadBlob(blob, 'wyniki_praktyczne_etap2.csv'),
    onError: () => toast.error('Nie udało się pobrać raportu'),
  });

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'score' || field === 'totalScore' ? 'desc' : 'asc');
    }
  };

  const handleDisqualifyClick = (sessionId: string) => {
    setConfirmingSessionId(sessionId);
  };

  const handleConfirmDisqualify = (sessionId: string) => {
    setConfirmingSessionId(null);
    disqualifyMutation.mutate({ sessionId, disqualified: true });
  };

  const handleCancelDisqualify = () => {
    setConfirmingSessionId(null);
  };

  const sorted = useMemo(
    () => (completers ? sortCompleters(completers, sortField, sortDir) : []),
    [completers, sortField, sortDir],
  );

  // Track rank only among non-disqualified (for medals)
  let activeRank = 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tabela wyników</h2>
          <p className="mt-1 text-sm text-gray-500">
            Ranking uczestników — wynik malejąco, czas rosnąco przy remisie
          </p>
        </div>
        {readOnly ? (
          <div className="flex gap-2">
            {[
              { label: 'Wyniki łączne', mut: exportFullMutation },
              { label: 'Test teor.', mut: exportTeorMutation },
              { label: 'Test prakt.', mut: exportPraktMutation },
            ].map(({ label, mut }) => (
              <button
                key={label}
                onClick={() => mut.mutate()}
                disabled={mut.isPending}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md shadow-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {mut.isPending ? 'Pobieranie…' : label}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => exportAllAnswersMutation.mutate()}
              disabled={exportAllAnswersMutation.isPending}
              className="px-4 py-2 text-sm font-semibold rounded-md shadow-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {exportAllAnswersMutation.isPending ? 'Generowanie...' : 'Eksport (wszystkie odpowiedzi)'}
            </button>
            <button
              onClick={() => exportWrongAnswersMutation.mutate()}
              disabled={exportWrongAnswersMutation.isPending}
              className="px-4 py-2 text-sm font-semibold rounded-md shadow-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {exportWrongAnswersMutation.isPending ? 'Generowanie...' : 'Eksport (złe odpowiedzi)'}
            </button>
          </div>
        )}
      </div>

      {/* Stage switcher */}
      <div className="flex flex-wrap gap-2">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'rounded-lg px-5 py-2 text-sm font-semibold transition-colors',
              activeTab === tab.id
                ? 'bg-primary-600 text-white shadow-sm'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Klasyfikacja — {tabLabel(activeTab)}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSpinner size="md" className="py-10" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Miejsce</TableHead>
                  <TableHead>Uczestnik</TableHead>
                  <TableHead>Szkoła</TableHead>
                  <TableHead>Email</TableHead>
                  <SortHeader
                    label={isStage2 ? 'Wynik teor.' : 'Wynik'}
                    field="score"
                    active={sortField}
                    dir={sortDir}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <SortHeader
                    label="Czas teor."
                    field="durationFromStartClickMs"
                    active={sortField}
                    dir={sortDir}
                    onSort={handleSort}
                    className="text-right"
                  />
                  {isStage2 && (
                    <>
                      <TableHead className="text-right whitespace-nowrap">Wynik prakt.</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Czas prakt.</TableHead>
                      <SortHeader
                        label="Wynik łączny"
                        field="totalScore"
                        active={sortField}
                        dir={sortDir}
                        onSort={handleSort}
                        className="text-right"
                      />
                    </>
                  )}
                  {isStage2 && <TableHead className="text-right whitespace-nowrap">Czas łączny</TableHead>}
                  {!readOnly && <TableHead className="text-right whitespace-nowrap">Czas całkowity</TableHead>}
                  <SortHeader
                    label="Opuszczenia"
                    field="focusEventCount"
                    active={sortField}
                    dir={sortDir}
                    onSort={handleSort}
                    className="text-center"
                  />
                  {!readOnly && (
                    <SortHeader
                      label="Dyskwalifikacja"
                      field="disqualified"
                      active={sortField}
                      dir={sortDir}
                      onSort={handleSort}
                      className="text-center"
                    />
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isStage2 ? (readOnly ? 10 : 12) : 9} className="py-10 text-center text-gray-500">
                      Brak ukończonych testów dla {tabLabel(activeTab)}
                    </TableCell>
                  </TableRow>
                ) : (
                  sorted.map((c) => {
                    const rank = c.disqualified ? -1 : activeRank++;
                    const isPending =
                      disqualifyMutation.isPending &&
                      disqualifyMutation.variables?.sessionId === c.sessionId;

                    return (
                      <TableRow
                        key={c.id}
                        className={cn(
                          c.disqualified
                            ? 'bg-red-50 opacity-70'
                            : rank < 3
                              ? 'bg-yellow-50 hover:bg-yellow-100'
                              : undefined,
                        )}
                      >
                        {/* Miejsce */}
                        <TableCell className="text-center font-bold text-lg">
                          {c.disqualified ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                              <Ban className="h-3 w-3" />
                              DYSK.
                            </span>
                          ) : (
                            getMedalIcon(rank)
                          )}
                        </TableCell>

                        {/* Uczestnik */}
                        <TableCell className={cn('font-medium', c.disqualified && 'line-through text-gray-400')}>
                          {c.firstName} {c.lastName}
                        </TableCell>

                        {/* Szkoła */}
                        <TableCell>
                          {c.school ? (
                            <span className={cn('text-xs bg-gray-100 rounded px-2 py-0.5', c.disqualified && 'opacity-50')}>
                              {c.school.name}
                            </span>
                          ) : '—'}
                        </TableCell>

                        {/* Email */}
                        <TableCell className={cn('text-sm text-gray-500', c.disqualified && 'opacity-50')}>
                          {c.email}
                        </TableCell>

                        {/* Wynik (teor. dla E2 / zwykły dla E1) */}
                        <TableCell className={cn('text-right font-mono font-bold text-lg', c.disqualified ? 'text-gray-400' : isStage2 ? 'text-blue-600' : 'text-primary-700')}>
                          {c.score ?? '—'}
                        </TableCell>

                        {/* Czas teor. — przeniesiony zaraz za wynik teor. */}
                        <TableCell className={cn('text-right font-mono text-sm', c.disqualified ? 'text-gray-400' : 'text-gray-500')}>
                          {formatDuration(c.durationFromStartClickMs)}
                        </TableCell>

                        {/* Etap 2: wynik praktyczny + czas + łączny */}
                        {isStage2 && (
                          <>
                            <TableCell className={cn('text-right font-mono font-bold text-lg', c.disqualified ? 'text-gray-400' : 'text-violet-600')}>
                              {c.practicalScore !== null && c.practicalScore !== undefined ? c.practicalScore : <span className="text-gray-300 text-sm font-normal">—</span>}
                            </TableCell>
                            <TableCell className={cn('text-right font-mono text-sm', c.disqualified ? 'text-gray-400' : 'text-gray-500')}>
                              {c.practicalDurationSeconds !== null && c.practicalDurationSeconds !== undefined
                                ? formatDuration(c.practicalDurationSeconds * 1000)
                                : <span className="text-gray-300">—</span>}
                            </TableCell>
                            <TableCell className={cn('text-right font-mono font-bold text-xl', c.disqualified ? 'text-gray-400' : 'text-primary-700')}>
                              {c.totalScore !== undefined ? c.totalScore : (c.score ?? '—')}
                            </TableCell>
                          </>
                        )}

                        {/* Czas łączny (teor. + prakt.) — stage 2 only */}
                        {isStage2 && (() => {
                          const totalMs = (c.durationFromStartClickMs ?? 0) + (c.practicalDurationSeconds ?? 0) * 1000;
                          return (
                            <TableCell className={cn('text-right font-mono text-sm', c.disqualified ? 'text-gray-400' : 'text-gray-600')}>
                              {(c.durationFromStartClickMs !== null || c.practicalDurationSeconds !== null)
                                ? formatDuration(totalMs)
                                : <span className="text-gray-300">—</span>}
                            </TableCell>
                          );
                        })()}

                        {/* Czas spędzony na zadaniach (admin only) */}
                        {!readOnly && (
                          <TableCell className={cn('text-right font-mono text-sm', c.disqualified ? 'text-gray-400' : 'text-gray-500')}>
                            {formatDuration(c.durationFromGlobalStartMs)}
                          </TableCell>
                        )}

                        {/* Opuszczenia */}
                        <TableCell className="text-center">
                          {c.focusEventCount > 0 ? (
                            <span className={cn(
                              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                              c.disqualified
                                ? 'bg-gray-100 text-gray-400'
                                : 'bg-amber-100 text-amber-700',
                            )}>
                              {c.focusEventCount}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-sm">—</span>
                          )}
                        </TableCell>

                        {/* Dyskwalifikacja */}
                        {!readOnly && <TableCell className="text-center">
                          {!c.sessionId ? (
                            <span className="text-gray-300 text-sm">—</span>
                          ) : confirmingSessionId === c.sessionId ? (
                            /* ── Inline confirm prompt ── */
                            <div className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs shadow-sm">

                              <button
                                onClick={() => handleConfirmDisqualify(c.sessionId!)}
                                className="ml-1 inline-flex items-center gap-0.5 rounded bg-red-600 px-2 py-0.5 text-white hover:bg-red-700"
                                title="Potwierdź dyskwalifikację"
                              >
                                Potwierdź
                              </button>
                              <button
                                onClick={handleCancelDisqualify}
                                className="inline-flex items-center gap-0.5 rounded bg-gray-200 px-2 py-0.5 text-gray-700 hover:bg-gray-300"
                                title="Anuluj"
                              >
                                Cofnij
                              </button>
                            </div>
                          ) : (
                            /* ── Normal action button ── */
                            <button
                              disabled={isPending}
                              onClick={() =>
                                c.disqualified
                                  ? disqualifyMutation.mutate({ sessionId: c.sessionId!, disqualified: false })
                                  : handleDisqualifyClick(c.sessionId!)
                              }
                              className={cn(
                                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50',
                                c.disqualified
                                  ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200',
                              )}
                              title={c.disqualified ? 'Cofnij dyskwalifikację' : 'Dyskwalifikuj'}
                            >
                              {isPending ? (
                                <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              ) : c.disqualified ? (
                                <>
                                  <Undo2 className="h-3 w-3" />
                                  Przywróć
                                </>
                              ) : (
                                <>
                                  <Ban className="h-3 w-3" />
                                  Dyskwalifikuj
                                </>
                              )}
                            </button>
                          )}
                        </TableCell>}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {sorted.length > 0 && (
        <p className="text-xs text-gray-400 text-right">
          {sorted.filter((c) => !c.disqualified).length} kwalifikujących
          {sorted.some((c) => c.disqualified) &&
            ` · ${sorted.filter((c) => c.disqualified).length} zdyskwalifikowanych`}
          {' '}— {tabLabel(activeTab)}
        </p>
      )}
    </div>
  );
};
